import { Rocket, Check, IndianRupee, Store, TrendingUp, ArrowRight, Infinity as InfinityIcon } from 'lucide-react';

const APPLY_URL = 'https://forms.gle/gsTG6UzS4kM4Pu848';

const OFFER = [
  { icon: InfinityIcon, title: 'No cap on brands', body: 'We are not limiting the cohort. Every brand that meets the bar gets in.' },
  { icon: Rocket, title: 'First 6 months: pure revenue share', body: 'No retainer. No setup fee. We only earn when you grow.' },
  { icon: TrendingUp, title: 'After 6 months: revenue share + retainer', body: 'Once the growth is proven, we move to a blended model.' },
  { icon: Check, title: 'OmniView Pro, free', body: 'Full platform access for the whole engagement, on us.' },
];

const ELIGIBILITY = [
  { icon: IndianRupee, label: 'Minimum \u20B91L monthly marketing budget', hint: 'Enough spend for us to move the needle' },
  { icon: TrendingUp, label: 'Minimum \u20B93L MRR', hint: 'A working product with real demand' },
  { icon: Store, label: 'An efficient webstore with existing hooks', hint: 'Conversion basics already in place' },
];

export const FlashGrowthSection = ({ isDark }) => (
  <section id="flash-growth" data-testid="flash-growth-section"
    className="py-20 sm:py-28 px-4 sm:px-8 relative overflow-hidden"
    style={{ background: isDark ? '#05070f' : '#04122e' }}>
    {/* deep field so the offer reads as a distinct, premium moment on the page */}
    <div aria-hidden className="absolute inset-0 pointer-events-none opacity-70" style={{
      background: 'radial-gradient(1100px 480px at 12% -10%, rgba(0,212,255,.22), transparent 60%), radial-gradient(760px 420px at 92% 110%, rgba(0,47,167,.55), transparent 60%)',
    }} />
    <div aria-hidden className="absolute inset-0 pointer-events-none opacity-[0.05]" style={{
      backgroundImage: 'linear-gradient(rgba(255,255,255,.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.6) 1px, transparent 1px)',
      backgroundSize: '46px 46px',
    }} />

    <div className="relative max-w-6xl mx-auto text-white">
      <div className="max-w-3xl">
        <span data-testid="flash-growth-eyebrow"
          className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-semibold tracking-[0.14em] uppercase mb-6"
          style={{ background: 'rgba(0,212,255,.14)', border: '1px solid rgba(0,212,255,.4)', color: '#7FE9FF' }}>
          <Rocket size={13} /> OmniView D2C Cohort 2026
        </span>

        <h2 className="text-3xl sm:text-5xl font-bold tracking-tight leading-[1.08] mb-5">
          The Flash Growth Batch
        </h2>
        <p className="text-base md:text-lg text-white/70 leading-relaxed mb-4">
          A six-month growth engagement for D2C brands that already work. We take the
          performance risk with you: for the first six months we are paid purely on a share
          of the revenue we generate. If you do not grow, we do not earn.
        </p>
        <p className="text-sm text-white/50 mb-12">
          Applications are open on a rolling basis and there is <strong className="text-white/80">no cap on the
          number of brands or agencies</strong> we accept. The only gate is the eligibility bar below.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 mb-14">
        {OFFER.map(o => {
          const Icon = o.icon;
          return (
            <div key={o.title} data-testid={`flash-offer-${o.title.split(' ')[0].toLowerCase()}`}
              className="rounded-2xl p-6 transition-all duration-200 hover:-translate-y-0.5"
              style={{ background: 'rgba(255,255,255,.05)', border: '1px solid rgba(255,255,255,.11)', backdropFilter: 'blur(14px)' }}>
              <div className="w-10 h-10 rounded-xl grid place-items-center mb-4"
                style={{ background: 'rgba(0,212,255,.14)', border: '1px solid rgba(0,212,255,.3)' }}>
                <Icon size={18} color="#7FE9FF" />
              </div>
              <h3 className="text-base md:text-lg font-semibold mb-1.5">{o.title}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{o.body}</p>
            </div>
          );
        })}
      </div>

      <div className="rounded-2xl p-7 sm:p-10"
        style={{ background: 'rgba(0,0,0,.28)', border: '1px solid rgba(255,255,255,.13)' }}>
        <div className="grid lg:grid-cols-[1.35fr_1fr] gap-10 items-center">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-[0.14em] text-white/45 mb-5">
              Are you eligible?
            </h3>
            <ul className="space-y-4" data-testid="flash-eligibility">
              {ELIGIBILITY.map(e => {
                const Icon = e.icon;
                return (
                  <li key={e.label} className="flex gap-3.5">
                    <span className="w-8 h-8 rounded-lg grid place-items-center flex-shrink-0 mt-0.5"
                      style={{ background: 'rgba(6,214,160,.14)', border: '1px solid rgba(6,214,160,.32)' }}>
                      <Icon size={15} color="#5FE3BE" />
                    </span>
                    <span>
                      <span className="block text-sm md:text-base font-medium">{e.label}</span>
                      <span className="block text-xs text-white/45 mt-0.5">{e.hint}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="lg:border-l lg:pl-10" style={{ borderColor: 'rgba(255,255,255,.12)' }}>
            <p className="text-sm text-white/60 leading-relaxed mb-6">
              Meet all three and you are in. Apply and we will come back to you with a
              growth plan for your brand, not a sales deck.
            </p>
            <a href={APPLY_URL} target="_blank" rel="noopener noreferrer"
              data-testid="flash-growth-apply"
              className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-full text-sm font-semibold transition-all duration-200 hover:gap-4"
              style={{ background: 'linear-gradient(135deg,#00D4FF,#0093C4)', color: '#04122e', boxShadow: '0 10px 30px -10px rgba(0,212,255,.6)' }}>
              Apply to the cohort <ArrowRight size={16} />
            </a>
            <p className="text-xs text-white/35 mt-4">
              Takes about 3 minutes. No card, no commitment.
            </p>
          </div>
        </div>
      </div>
    </div>
  </section>
);
